import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PAGE_TITLES } from "~/configs/pageTitles";
import { useLinkDetailPageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { TaskAttributes, Task as TaskType } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import Button from "../../../components/ui/button/Button";
import Card from "../../../components/ui/card/Card";
import styles from "./OutreachLinkDetailPage.module.scss";

// Define the response type that matches the actual API response
interface TaskResponse {
  task: TaskType;
}

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const getStatusClass = () => {
    switch (status) {
      case "DONE":
        return styles.statusDone;
      case "DOING":
        return styles.statusDoing;
      case "CANCELLED":
        return styles.statusCancelled;
      case "TODO":
      default:
        return styles.statusTodo;
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass()}`}>
      {status}
    </span>
  );
};

const OutreachLinkDetailPage = () => {
  const { id: siteId, taskId } = useParams();

  if (!siteId) {
    throw Error("No site id provided");
  }

  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [notes, setNotes] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Fetch outreach link data
  const {
    data: taskResponse,
    isLoading,
    error,
  } = useQuery<TaskResponse>({
    queryKey: ["outreachTask", siteId, taskId],
    queryFn: async () => {
      try {
        // Convert IDs to numbers and validate
        const numericSiteId = Number(siteId);
        const numericTaskId = Number(taskId);

        if (isNaN(numericSiteId) || isNaN(numericTaskId)) {
          throw new Error("Invalid site ID or task ID");
        }

        // Use the API endpoint to get a single task
        const response = await apiClient.sites.tasksDetail2(
          numericSiteId,
          numericTaskId,
        );

        if (!response.data) {
          throw new Error("No data returned from API");
        }

        // The API returns the data in a format we don't expect, so we have to cast it
        return response.data as unknown as TaskResponse;
      } catch (error) {
        console.error("Error fetching task:", error);
        throw error;
      }
    },
    enabled: !!siteId && !!taskId,
    retry: 2, // Retry the query up to 2 times if it fails
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Extract the actual task data from the response
  const task = taskResponse?.task;

  // Set the page title with the enhanced pattern
  useLinkDetailPageTitle(task?.link_domain, PAGE_TITLES.communityLinks);

  // Update notes state when task data is loaded
  useEffect(() => {
    if (task?.notes) {
      setNotes(task.notes);
    }
  }, [task]);

  const handleFinishOutreach = async () => {
    if (!task || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const updateData: TaskAttributes = {
        status: "DONE",
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );

      // Navigate back to the outreach links list
      navigate(routes.site.links.outreach({ id: siteId }));
    } catch (error) {
      console.error("Error updating outreach link:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!task || isCancelling) return;

    setIsCancelling(true);

    try {
      const updateData: TaskAttributes = {
        //@ts-expect-error @TODO: not clear why this is needed
        status: "CANCELLED",
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );

      navigate(routes.site.links.outreach({ id: siteId }));
    } catch (error) {
      console.error("Error cancelling outreach link:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!task || isSavingNotes) return;

    setIsSavingNotes(true);

    try {
      const updateData: TaskAttributes = {
        notes: notes,
      };

      await apiClient.sites.tasksPartialUpdate(
        Number(siteId),
        Number(taskId),
        updateData,
      );
    } catch (error) {
      console.error("Error saving notes:", error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Display loading state
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: "20px" }}>
            Loading community link details...
          </div>
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div style={{ textAlign: "center", color: "red" }}>
          <div style={{ marginBottom: "20px", fontWeight: "bold" }}>
            Error loading community link details
          </div>
          <div>
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </div>
          <Button
            style={{ marginTop: "20px" }}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.contentLayout}>
        <div className={styles.mainContent}>
          <Card>
            <div className={styles.linkDetailsHeader}>
              <h1 className={styles.title}>
                {task?.link_domain && (
                  <div className={styles.faviconContainer}>
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${task.link_domain}&sz=32`}
                      alt=""
                      className={styles.favicon}
                      onError={(e) => {
                        // Replace with generic icon if favicon fails to load
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32' height='32'%3E%3Cpath fill='%23ccc' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                )}
                {task && task.link_title ? task.link_title : "Community Link"}
              </h1>
            </div>

            <div className={styles.messageSection}>
              <p>
                Community opportunities help increase your visibility in
                relevant discussions across forums, Q&A platforms, and online
                communities which are tracked by Google and used for LLM model
                training and agents. The goal is to contribute useful expertise
                where your audience is already active.
              </p>

              <div className={styles.contributorSection}>
                <div className={styles.contributorInstructionsList}>
                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>1</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Review the discussion carefully
                      </div>
                      <div className={styles.stepDescription}>
                        Read the thread or article in full so you understand the
                        context before participating.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>2</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Contribute meaningful insight
                      </div>
                      <div className={styles.stepDescription}>
                        Add a thoughtful, relevant response that genuinely helps
                        the discussion and reflects your expertise.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>3</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Strengthen your profile presence
                      </div>
                      <div className={styles.stepDescription}>
                        Where possible, include your website in your profile or
                        bio to improve discoverability.
                      </div>
                    </div>
                  </div>

                  <div className={styles.contributorInstructionItem}>
                    <div className={styles.stepNumber}>4</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepTitle}>
                        Add a link only when it adds value
                      </div>
                      <div className={styles.stepDescription}>
                        If it is appropriate and useful in context, include a
                        relevant link to your site using the recommended page
                        and keyword focus where applicable.
                        <br />
                        Focus keyword:{" "}
                        <span className={styles.keywordHighlight}>
                          {task && task.focus_keyword ? task.focus_keyword : ""}
                        </span>
                        <br />
                        Ranking URL:{" "}
                        <span className={styles.urlHighlight}>
                          {task && task.ranking_url ? task.ranking_url : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={styles.emailTemplate}
                  style={{ borderLeft: "4px solid var(--service-red2)" }}
                >
                  Avoid low-value or promotional comments made solely to place a
                  link. Contributions should be useful on their own and aligned
                  with the expectations of the community.
                </div>
              </div>
            </div>
          </Card>

          <div className={styles.notesCard}>
            <h2>Notes</h2>
            <textarea
              className={styles.notesTextarea}
              placeholder="Place your notes here for the task"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button
              className={styles.notesButton}
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
            >
              {isSavingNotes ? "Saving..." : "Save Notes"}
            </Button>
          </div>
        </div>

        <div className={styles.sideContent}>
          <Card>
            <div className={styles.detailsSection}>
              <h2>Details</h2>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>LINK URL:</span>
                  <span className={styles.detailValue}>
                    {task && task.link_page_url ? (
                      <a
                        href={
                          task.link_page_url.startsWith("http")
                            ? task.link_page_url
                            : `https://${task.link_page_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.link_page_url}
                      </a>
                    ) : task && task.link_domain ? (
                      <a
                        href={`https://${task.link_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {`https://${task.link_domain}`}
                      </a>
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>TITLE:</span>
                  <span
                    className={styles.detailValue}
                    title={task?.link_title || ""}
                  >
                    {task && task.link_title
                      ? task.link_title
                      : "(No title available)"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>DOMAIN:</span>
                  <span className={styles.detailValue}>
                    {task && task.link_domain
                      ? task.link_domain
                      : "(No domain available)"}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>CATEGORY:</span>
                  <span className={styles.detailValue}>
                    {task && task.category ? task.category : ""}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>KEYWORD:</span>
                  <span className={styles.detailValue}>
                    {task && task.focus_keyword
                      ? task.focus_keyword.toLowerCase()
                      : ""}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>RANKING URL:</span>
                  <span className={styles.detailValue}>
                    {task && task.ranking_url ? (
                      <a
                        href={
                          task.ranking_url.startsWith("http")
                            ? task.ranking_url
                            : `https://${task.ranking_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {task.ranking_url}
                      </a>
                    ) : (
                      "-"
                    )}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>STATUS:</span>
                  <span className={styles.detailValue}>
                    <StatusBadge
                      status={task && task.status ? task.status : "TODO"}
                    />
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <Button
                onClick={handleFinishOutreach}
                disabled={isSubmitting || task?.status === "DONE"}
              >
                {isSubmitting ? "Saving..." : "Finish Community Link"}
              </Button>
              <Button outlined onClick={handleCancel} disabled={isCancelling}>
                {isCancelling ? "Cancelling..." : "Cancel"}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OutreachLinkDetailPage;
